import { IconButton, InputAdornment } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { ReactNode, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { ReactComponent as FPSIcon } from '@/assets/icons/fps-big.svg';
import { BanksSelection, ClosableDialog, HeaderTitle, Label, Space, TextFieldInput } from '@/components';
import { Bank } from '@/components/input/BanksSelection';
import { Value } from '@/model/common';
import { makeSelectIsMobileView } from '@/model/global/selectors';
import { makeSelectReceiverBank, makeSelectReceiverBanks } from '@/model/withdrawal/selectors';
import BankReadonlyValue from '@/pages/withdrawal/components/BankReadonlyValue';
import { Func } from '@/utils/ts';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    search: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
    searchMobile: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  }),
);

interface BankInfoProps {
  defaultComponent: ReactNode;
  value: Bank;
  priority?: Bank;
}

const BankInfo: React.FC<BankInfoProps> = ({ defaultComponent, value, priority }: BankInfoProps) => (
  <Space direction="vertical" size={0} style={{ justifyContent: 'center' }} className="ym-hide-content">
    {defaultComponent}
    {priority?.id === value.id && (
      <Label>
        <FormattedMessage id="withdrawal.fields.bank.priority.label" />
      </Label>
    )}
  </Space>
);

interface BanksSelectionDialogProps {
  'data-test'?: string;
  onSelect: Func<[string]>;
  onClose: Func;
}

const selectReceiverBanks = makeSelectReceiverBanks();
const selectIsMobile = makeSelectIsMobileView();

const BanksSelectionDialog: React.FC<BanksSelectionDialogProps> = ({ 'data-test': dataTest, onSelect, onClose }) => {
  const classes = useStyles();
  const receiverBanks = useSelector(selectReceiverBanks);
  const isMobile = useSelector(selectIsMobile);
  const priorityBank = useMemo(() => receiverBanks.filter(({ priority }) => !!priority)[0], [receiverBanks]);
  const sortedBanks = useMemo(
    () => [...(priorityBank ? [priorityBank] : []), ...receiverBanks.filter(({ priority }) => !priority)],
    [receiverBanks, priorityBank],
  );

  return (
    <ClosableDialog
      data-test={dataTest && `${dataTest}-dialog`}
      onClose={onClose}
      visible
      title={
        <HeaderTitle
          data-test={dataTest && `${dataTest}-header`}
          title={<FormattedMessage id="withdrawal.fields.bank.dialog.title" />}
          postfix={!isMobile && <FPSIcon />}
        />
      }
    >
      <BanksSelection
        data-test={dataTest && `${dataTest}-selection`}
        value={sortedBanks}
        onSelect={onSelect}
        LabelComponent={BankInfo}
        searchClassName={isMobile ? classes.searchMobile : classes.search}
        bankItemClassName={isMobile ? classes.searchMobile : classes.search}
      />
    </ClosableDialog>
  );
};

export interface BankItemProps {
  'data-test'?: string;
  value?: Value<string>;
  isCredit?: boolean;
  readonly?: boolean;
  onChange?: (value: string) => Promise<void> | void;
}

export const BankItem: React.FC<BankItemProps> = ({
  value,
  isCredit = true,
  onChange,
  readonly,
  'data-test': dataTest,
}) => {
  const bankId = value?.value;
  const [banksVisible, showBanks] = useState<boolean>(false);
  const selectBank = useMemo(() => (bankId ? makeSelectReceiverBank(bankId) : (): undefined => undefined), [bankId]);
  const bank = useSelector(selectBank);
  return readonly ? (
    <BankReadonlyValue isCredit={isCredit} value={bankId} data-test={dataTest && `${dataTest}-value`} />
  ) : (
    <>
      <TextFieldInput
        data-test={dataTest && `${dataTest}-input`}
        InputProps={{
          className: 'ym-hide-content',
          readOnly: true,
          inputProps: {
            tabIndex: -1,
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                data-test={dataTest && `${dataTest}-search`}
                onClick={(): void => {
                  showBanks(true);
                }}
              >
                <KeyboardArrowDownIcon style={{ cursor: 'pointer' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        value={{ value: bank?.name, hasError: !!value?.hasError }}
        messages={{
          label: isCredit ? (
            <FormattedMessage id="withdrawal.fields.bank.label.receiver" />
          ) : (
            <FormattedMessage id="withdrawal.fields.bank.label.sender" />
          ),
          error: <FormattedMessage id="withdrawal.fields.bank.error" />,
          helperText: bank?.priority && <FormattedMessage id="withdrawal.fields.bank.priority.label" />,
        }}
      />
      {banksVisible && onChange && (
        <BanksSelectionDialog
          data-test={dataTest && `${dataTest}-dialog`}
          onSelect={async (selectedBank: string): Promise<void> => {
            await onChange(selectedBank);
            showBanks(false);
          }}
          onClose={(): void => showBanks(false)}
        />
      )}
    </>
  );
};

export default BankItem;
